/**
 * Client data models.
 */

import {
  BaseEntity,
  TimestampMixin,
  SearchableEntity,
  ApprovalEntity,
  ActiveEntity,
  ContactEntity,
  AddressEntity,
  ImageEntity,
  DEFAULT_STRINGS,
} from './base';
import type { ProfitPlus2k8ClientIntegration } from './integrations/profitPlus2k8';

export interface Representative {
  fullName: string;
  email: string;
  mobile: string;
  identityNumber: string;
}

export interface ClientBase
  extends SearchableEntity,
    ApprovalEntity,
    ActiveEntity,
    ContactEntity,
    ImageEntity,
    AddressEntity {
  code: string;
  rif: string; // Tax identification number
  businessName?: string;
  legalName?: string;
  officePhone?: string;
  fiscalAddress?: string;
  shippingAddress?: string;
  responsible?: string;
  fax?: string;
  alternativeShippingAddress?: string;
  description?: string;
  website?: string;
  creatorSalesPerson?: string; // Creator sales person email

  // MVP: Essential financial fields only
  creditLimit?: number; // Maximum credit allowed
  creditTerms?: number; // Payment terms in days (e.g., 30, 60, 90)

  // MVP: Basic business classification
  customerType?: 'wholesale' | 'retail' | 'distributor' | 'reseller';

  // MVP: Basic sales management
  assignedSalesRep?: string; // Primary sales representative ID
  salesZoneId?: string;
  salesZoneName?: string;

  /** Profit Plus 2k8 (ERP): one field per integration – native fields (dbo.clientes) + syncedAt. */
  profitPlus2k8?: ProfitPlus2k8ClientIntegration;

  // ===== FUTURE VERSIONS - COMMENTED OUT =====
  // TODO: v2.0 - Advanced Financial Management
  // currentBalance?: number; // Current outstanding balance
  // creditRating?: 'A' | 'B' | 'C' | 'D'; // Credit risk assessment

  // TODO: v2.0 - Advanced Pricing & Discounts
  // pricingTier?: 'wholesale' | 'retail' | 'distributor'; // Customer pricing level
  // discountPercentage?: number; // Custom discount rate (0-100)
  // specialPricing?: boolean; // Whether client gets special pricing
  // priceListId?: string; // Reference to specific price list

  // TODO: v2.0 - Advanced Business Classification
  // businessSize?: 'small' | 'medium' | 'large' | 'enterprise';
  // annualVolume?: number; // Expected annual purchase volume
  // industry?: string; // Industry sector

  // TODO: v2.0 - Advanced Sales & Territory Management
  // salesTerritory?: string; // Geographic territory
  // salesChannel?: 'direct' | 'online' | 'phone' | 'field';
  // lastOrderDate?: Date; // When they last placed an order
  // totalOrdersValue?: number; // Lifetime value

  // TODO: v2.0 - Compliance & Legal
  // taxExempt?: boolean; // Tax exemption status
  // taxExemptNumber?: string; // Tax exemption certificate number
  // businessLicense?: string; // Business license number
  // complianceNotes?: string; // Any compliance requirements

  // TODO: v2.0 - Communication Preferences
  // preferredContactMethod?: 'email' | 'phone' | 'sms' | 'mail';
  // marketingOptIn?: boolean; // Marketing communications consent
  // languagePreference?: string; // Preferred language
  // timeZone?: string; // Client's timezone
}

export interface Client extends ClientBase, BaseEntity, TimestampMixin {
  legalRepresentatives?: Representative[];
}

export interface ClientCreate extends Omit<ClientBase, 'keywords'> {
  // For creating new clients - keywords are generated automatically
}

export interface ClientUpdate extends Partial<ClientBase> {
  legalRepresentatives?: Representative[];
  updated?: Date;
}

// Default values for clients
export const CLIENT_DEFAULTS = {
  code: DEFAULT_STRINGS.UNDEFINED,
  legalName: DEFAULT_STRINGS.UNDEFINED,
  city: DEFAULT_STRINGS.UNDEFINED,
  coordination: '',
  active: true,
  approvalState: 'approved' as const,
  mobile: DEFAULT_STRINGS.UNDEFINED,
  officePhone: DEFAULT_STRINGS.UNDEFINED,
  fiscalAddress: DEFAULT_STRINGS.UNDEFINED,
  shippingAddress: DEFAULT_STRINGS.UNDEFINED,
  municipality: '',
  referencePoint: '',
  zone: DEFAULT_STRINGS.UNDEFINED,
  salesZoneId: '',
  salesZoneName: '',
  taxpayer: DEFAULT_STRINGS.UNDEFINED,
  responsible: DEFAULT_STRINGS.UNDEFINED,
  type: DEFAULT_STRINGS.UNDEFINED,
  typeDescription: DEFAULT_STRINGS.UNDEFINED,
  fax: DEFAULT_STRINGS.UNDEFINED,
  segment: DEFAULT_STRINGS.UNDEFINED,
  zoneDescription: DEFAULT_STRINGS.UNDEFINED,
  salesRepDescription: DEFAULT_STRINGS.UNDEFINED,
  alternativeShippingAddress: DEFAULT_STRINGS.UNDEFINED,
  description: DEFAULT_STRINGS.UNDEFINED,
  incomeCode: DEFAULT_STRINGS.UNDEFINED,
  email: DEFAULT_STRINGS.UNDEFINED,
  branchCode: DEFAULT_STRINGS.UNDEFINED,

  // MVP: Essential financial defaults
  creditLimit: 0,
  creditTerms: 30, // Default 30 days

  // MVP: Basic business classification defaults
  customerType: 'wholesale' as const,

  // ===== FUTURE VERSIONS - COMMENTED OUT =====
  // TODO: v2.0 - Advanced Financial Management defaults
  // currentBalance: 0,
  // creditRating: 'B' as const,

  // TODO: v2.0 - Advanced Pricing & Discounts defaults
  // pricingTier: 'wholesale' as const,
  // discountPercentage: 0,
  // specialPricing: false,
  // priceListId: DEFAULT_STRINGS.UNDEFINED,

  // TODO: v2.0 - Advanced Business Classification defaults
  // businessSize: 'medium' as const,
  // annualVolume: 0,
  // industry: DEFAULT_STRINGS.UNDEFINED,

  // TODO: v2.0 - Advanced Sales & Territory Management defaults
  // salesTerritory: DEFAULT_STRINGS.UNDEFINED,
  // salesChannel: 'direct' as const,
  // lastOrderDate: undefined,
  // totalOrdersValue: 0,

  // TODO: v2.0 - Compliance & Legal defaults
  // taxExempt: false,
  // taxExemptNumber: DEFAULT_STRINGS.UNDEFINED,
  // businessLicense: DEFAULT_STRINGS.UNDEFINED,
  // complianceNotes: DEFAULT_STRINGS.UNDEFINED,

  // TODO: v2.0 - Communication Preferences defaults
  // preferredContactMethod: 'email' as const,
  // marketingOptIn: false,
  // languagePreference: 'en',
  // timeZone: 'UTC',
} as const;

// Client search and filter types
export interface ClientFilters {
  searchTerm?: string;
  city?: string;
  zone?: string;
  type?: string;
  active?: boolean;
  approvalState?: 'approved' | 'pending' | 'rejected';

  // MVP: Essential filters only
  creditLimitMin?: number;
  creditLimitMax?: number;
  customerType?: 'wholesale' | 'retail' | 'distributor' | 'reseller';

  // ===== FUTURE VERSIONS - COMMENTED OUT =====
  // TODO: v2.0 - Advanced Financial filters
  // creditRating?: 'A' | 'B' | 'C' | 'D';
  // currentBalanceMin?: number;
  // currentBalanceMax?: number;

  // TODO: v2.0 - Advanced Pricing filters
  // pricingTier?: 'wholesale' | 'retail' | 'distributor';
  // specialPricing?: boolean;

  // TODO: v2.0 - Advanced Business classification filters
  // businessSize?: 'small' | 'medium' | 'large' | 'enterprise';
  // industry?: string;

  // TODO: v2.0 - Advanced Sales management filters
  // salesTerritory?: string;
  // salesChannel?: 'direct' | 'online' | 'phone' | 'field';

  // TODO: v2.0 - Advanced Compliance filters
  // taxExempt?: boolean;
}

export interface ClientQueryResult {
  clients: Client[];
  lastDoc?: any; // Firestore document snapshot
  hasMore: boolean;
  totalCount?: number;
}

// Additional utility types for enhanced client management
export interface ClientSummary {
  id: string;
  code: string;
  businessName: string;
  rif: string;
  creditLimit: number;
  currentBalance: number;
  creditRating: 'A' | 'B' | 'C' | 'D';
  lastOrderDate?: Date;
  totalOrdersValue: number;
  assignedSalesRep?: string;
  active: boolean;
}

export interface ClientFinancialInfo {
  creditLimit: number;
  creditTerms: number;
}
